import React, { useState, useEffect } from 'react';
import { PlayerCreateInput } from '../../control-room/services/playersService';
import { supabase } from '../../../shared/lib/supabase';

interface SquadAssignment {
  tournamentId: string;
  seasonId: string;
  tournamentName: string;
  seasonName: string;
}

interface AddPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    playerData: PlayerCreateInput;
    organisationId: string;
    teamId?: string;
    squadAssignments?: SquadAssignment[];
  }) => Promise<void>;
  organisationId: string;
}

const AddPlayerModal: React.FC<AddPlayerModalProps> = ({
  isOpen,
  onClose,
  onSave,
  organisationId
}) => {
  const [formData, setFormData] = useState<PlayerCreateInput>({
    full_name: '',
    position: null,
    date_of_birth: null,
    nationality: null,
    profile_image: null,
    height_cm: null,
    weight_kg: null,
    preferred_foot: null,
    kit_number: null,
    bio: null,
    social_media: null,
    contact_info: null,
    is_active: true,
    data_source: 'custom'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  
  // Squad assignment fields
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [squadAssignments, setSquadAssignments] = useState<SquadAssignment[]>([]);
  const [tempTournament, setTempTournament] = useState<string>('');
  const [tempSeason, setTempSeason] = useState<string>('');
  
  // Options for dropdowns
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [seasons, setSeasons] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Load tournaments, seasons, and teams when modal opens
  useEffect(() => {
    if (isOpen && organisationId) {
      loadOptions();
    }
  }, [isOpen, organisationId]);

  const loadOptions = async () => {
    if (!organisationId) return;
    
    setLoadingOptions(true);
    try {
      const [tournamentRes, seasonRes, teamRes] = await Promise.all([
        supabase
          .from('tournament_access')
          .select('tournament:tournament_id(id, name)')
          .eq('organisation_id', organisationId)
          .eq('is_active', true),
        supabase
          .from('season_access')
          .select('season:season_id(id, name)')
          .eq('organisation_id', organisationId)
          .eq('active', true),
        supabase
          .from('team_access')
          .select('team:team_id(id, name)')
          .eq('organisation_id', organisationId)
          .eq('active', true)
      ]);

      if (tournamentRes.data) {
        setTournaments(tournamentRes.data.map((t: any) => t.tournament).filter(Boolean));
      }
      if (seasonRes.data) {
        setSeasons(seasonRes.data.map((s: any) => s.season).filter(Boolean));
      }
      if (teamRes.data) {
        setTeams(teamRes.data.map((t: any) => t.team).filter(Boolean));
      }
    } catch (error) {
      console.error('Error loading options:', error);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        full_name: '',
        position: null,
        date_of_birth: null,
        nationality: null,
        profile_image: null,
        height_cm: null,
        weight_kg: null,
        preferred_foot: null,
        kit_number: null,
        bio: null,
        social_media: null,
        contact_info: null,
        is_active: true,
        data_source: 'custom'
      });
      setSelectedTeam('');
      setSquadAssignments([]);
      setTempTournament('');
      setTempSeason('');
      setErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof PlayerCreateInput, value: string | boolean | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.full_name?.trim()) {
      newErrors.full_name = 'Player name is required';
    }

    // Validate kit number if provided
    if (formData.kit_number !== null && formData.kit_number !== undefined) {
      if (formData.kit_number < 1 || formData.kit_number > 99) {
        newErrors.kit_number = 'Kit number must be between 1 and 99';
      }
    }

    // Validate height if provided
    if (formData.height_cm !== null && formData.height_cm !== undefined) {
      if (formData.height_cm < 100 || formData.height_cm > 250) {
        newErrors.height_cm = 'Height must be between 100 and 250 cm';
      }
    }

    // Validate weight if provided
    if (formData.weight_kg !== null && formData.weight_kg !== undefined) {
      if (formData.weight_kg < 30 || formData.weight_kg > 200) {
        newErrors.weight_kg = 'Weight must be between 30 and 200 kg';
      }
    }

    // Validate date if provided
    if (formData.date_of_birth) {
      const date = new Date(formData.date_of_birth);
      if (isNaN(date.getTime())) {
        newErrors.date_of_birth = 'Invalid date format';
      } else if (date > new Date()) {
        newErrors.date_of_birth = 'Date of birth cannot be in the future';
      }
    }

    // Validate squad assignments if team is selected
    if (selectedTeam && squadAssignments.length > 0) {
      // Each assignment should have both tournament and season
      squadAssignments.forEach((assignment, index) => {
        if (!assignment.tournamentId || !assignment.seasonId) {
          newErrors[`squad_${index}`] = 'Each assignment must have both tournament and season';
        }
      });
    }

    // Normalize JSON fields
    const normalizeJson = (val: any) => {
      if (typeof val === 'string' && val.trim() !== '') {
        try {
          return JSON.parse(val);
        } catch (_e) {
          return val;
        }
      }
      return val;
    };

    formData.social_media = normalizeJson(formData.social_media) as any;
    formData.contact_info = normalizeJson(formData.contact_info) as any;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        playerData: formData,
        organisationId,
        teamId: selectedTeam || undefined,
        squadAssignments: selectedTeam && squadAssignments.length > 0 ? squadAssignments : undefined
      });
      onClose();
    } catch (error) {
      console.error('Error creating player:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSquadAssignment = () => {
    if (!tempTournament || !tempSeason) {
      setErrors(prev => ({ ...prev, tempAssignment: 'Please select both tournament and season' }));
      return;
    }

    const tournament = tournaments.find(t => t.id === tempTournament);
    const season = seasons.find(s => s.id === tempSeason);

    if (!tournament || !season) {
      setErrors(prev => ({ ...prev, tempAssignment: 'Invalid tournament or season selected' }));
      return;
    }

    // Check if this combination already exists
    const exists = squadAssignments.some(
      sa => sa.tournamentId === tempTournament && sa.seasonId === tempSeason
    );

    if (exists) {
      setErrors(prev => ({ ...prev, tempAssignment: 'This tournament/season combination is already added' }));
      return;
    }

    setSquadAssignments(prev => [...prev, {
      tournamentId: tempTournament,
      seasonId: tempSeason,
      tournamentName: tournament.name,
      seasonName: season.name
    }]);

    setTempTournament('');
    setTempSeason('');
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.tempAssignment;
      return newErrors;
    });
  };

  const handleRemoveSquadAssignment = (index: number) => {
    setSquadAssignments(prev => prev.filter((_, i) => i !== index));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Add New Player</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Player Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Player Name *
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter player name"
              />
              {errors.full_name && (
                <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>
              )}
            </div>

            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Position
              </label>
              <select
                value={formData.position || ''}
                onChange={(e) => handleInputChange('position', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select position...</option>
                <option value="Goalkeeper">Goalkeeper</option>
                <option value="Defender">Defender</option>
                <option value="Midfielder">Midfielder</option>
                <option value="Forward">Forward</option>
              </select>
            </div>

            {/* Jersey Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jersey Number
              </label>
              <input
                type="number"
                min="1"
                max="99"
                value={formData.kit_number || ''}
                onChange={(e) => handleInputChange('kit_number', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter jersey number"
              />
              {errors.kit_number && (
                <p className="text-red-500 text-xs mt-1">{errors.kit_number}</p>
              )}
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                value={formData.date_of_birth || ''}
                onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.date_of_birth && (
                <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>
              )}
            </div>

            {/* Nationality */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nationality
              </label>
              <input
                type="text"
                value={formData.nationality || ''}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter nationality"
              />
            </div>

            {/* Height */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Height (cm)
              </label>
              <input
                type="number"
                min="100"
                max="250"
                value={formData.height_cm || ''}
                onChange={(e) => handleInputChange('height_cm', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter height in cm"
              />
              {errors.height_cm && (
                <p className="text-red-500 text-xs mt-1">{errors.height_cm}</p>
              )}
            </div>

            {/* Weight */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (kg)
              </label>
              <input
                type="number"
                min="30"
                max="200"
                value={formData.weight_kg || ''}
                onChange={(e) => handleInputChange('weight_kg', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter weight in kg"
              />
              {errors.weight_kg && (
                <p className="text-red-500 text-xs mt-1">{errors.weight_kg}</p>
              )}
            </div>

          </div>

          {/* Optional Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Preferred Foot */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Foot</label>
              <select
                value={formData.preferred_foot || ''}
                onChange={(e) => handleInputChange('preferred_foot', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
                <option value="both">Both</option>
              </select>
            </div>

            {/* Profile Image URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Profile Image URL</label>
              <input
                type="text"
                value={formData.profile_image || ''}
                onChange={(e) => handleInputChange('profile_image', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>

            {/* Social Media (JSON) */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Social Media (JSON)</label>
              <textarea
                value={typeof formData.social_media === 'string' ? formData.social_media : (formData.social_media ? JSON.stringify(formData.social_media, null, 2) : '')}
                onChange={(e) => handleInputChange('social_media', e.target.value || null)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                placeholder='{"twitter":"@handle"}'
              />
            </div>

            {/* Contact Info (JSON) */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Info (JSON)</label>
              <textarea
                value={typeof formData.contact_info === 'string' ? formData.contact_info : (formData.contact_info ? JSON.stringify(formData.contact_info, null, 2) : '')}
                onChange={(e) => handleInputChange('contact_info', e.target.value || null)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                placeholder='{"phone":"+1..."}'
              />
            </div>

            {/* Bio */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <textarea
                value={formData.bio || ''}
                onChange={(e) => handleInputChange('bio', e.target.value || null)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Short player bio"
              />
            </div>
          </div>

          {/* Squad Assignment Section */}
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Optional: Assign to Squad</h3>
            
            {/* Team Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team
              </label>
              <select
                value={selectedTeam}
                onChange={(e) => {
                  setSelectedTeam(e.target.value);
                  if (!e.target.value) {
                    setSquadAssignments([]);
                  }
                }}
                disabled={loadingOptions}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">Select team...</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Add Tournament/Season Pairs */}
            {selectedTeam && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Tournament/Season Assignments
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Tournament */}
                  <div>
                    <select
                      value={tempTournament}
                      onChange={(e) => {
                        setTempTournament(e.target.value);
                        if (errors.tempAssignment) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.tempAssignment;
                            return newErrors;
                          });
                        }
                      }}
                      disabled={loadingOptions}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">Select tournament...</option>
                      {tournaments.map((tournament) => (
                        <option key={tournament.id} value={tournament.id}>
                          {tournament.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Season */}
                  <div>
                    <select
                      value={tempSeason}
                      onChange={(e) => {
                        setTempSeason(e.target.value);
                        if (errors.tempAssignment) {
                          setErrors(prev => {
                            const newErrors = { ...prev };
                            delete newErrors.tempAssignment;
                            return newErrors;
                          });
                        }
                      }}
                      disabled={loadingOptions}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="">Select season...</option>
                      {seasons.map((season) => (
                        <option key={season.id} value={season.id}>
                          {season.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Add Button */}
                  <div>
                    <button
                      type="button"
                      onClick={handleAddSquadAssignment}
                      className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                    >
                      + Add
                    </button>
                  </div>
                </div>
                {errors.tempAssignment && (
                  <p className="text-red-500 text-xs mt-1">{errors.tempAssignment}</p>
                )}
              </div>
            )}

            {/* List of Added Assignments */}
            {selectedTeam && squadAssignments.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Added Assignments ({squadAssignments.length})
                </label>
                <div className="space-y-2">
                  {squadAssignments.map((assignment, index) => (
                    <div
                      key={`${assignment.tournamentId}-${assignment.seasonId}`}
                      className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-700">
                        {assignment.tournamentName} • {assignment.seasonName}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveSquadAssignment(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedTeam && (
              <p className="text-xs text-gray-500">
                Select a team and add multiple tournament/season combinations. Each combination will create a separate squad entry.
              </p>
            )}
          </div>

          {/* Active Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Active Status
            </label>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleInputChange('is_active', !formData.is_active)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  formData.is_active ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                    formData.is_active ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">
                {formData.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              {isSaving ? 'Creating...' : 'Add Player'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPlayerModal;

